<script lang="ts">
	import { _ } from 'svelte-i18n';
	import Header from '$lib/components/sections/Header.svelte';
	import Footer from '$lib/components/sections/Footer.svelte';
	import SeoContent from '$lib/components/sections/SeoContent.svelte';
	import Button from '$lib/components/primitives/Button.svelte';
	import Card from '$lib/components/primitives/Card.svelte';
	import Icon from '$lib/components/primitives/Icon.svelte';

	const features = [
		{
			icon: 'clock',
			title: $_('products.mobile.features.timeTracking.title'),
			description: $_('products.mobile.features.timeTracking.description')
		},
		{
			icon: 'play',
			title: $_('products.mobile.features.timer.title'),
			description: $_('products.mobile.features.timer.description')
		},
		{
			icon: 'calendar',
			title: $_('products.mobile.features.calendar.title'),
			description: $_('products.mobile.features.calendar.description')
		},
		{
			icon: 'folder',
			title: $_('products.mobile.features.projects.title'),
			description: $_('products.mobile.features.projects.description')
		},
		{
			icon: 'file-text',
			title: $_('products.mobile.features.manual.title'),
			description: $_('products.mobile.features.manual.description')
		},
		{
			icon: 'refresh-cw',
			title: $_('products.mobile.features.sync.title'),
			description: $_('products.mobile.features.sync.description')
		}
	];
</script>

<svelte:head>
	<title>{$_('products.mobile.meta.title')}</title>
	<meta name="description" content={$_('products.mobile.meta.description')} />
	<meta property="og:title" content="{$_('products.mobile.meta.title')}" />
	<meta property="og:description" content={$_('products.mobile.meta.description')} />
</svelte:head>

<Header />

<div class="product-page">
	<!-- Hero Section -->
	<section class="hero">
		<div class="container">
			<div class="hero__content">
				<div class="hero__badge">
					<Icon name="smartphone" size={16} />
					<span>{$_('products.mobile.badge')}</span>
				</div>
				<h1 class="hero__title">
					{$_('products.mobile.hero.title')}
				</h1>
				<p class="hero__subtitle">
					{$_('products.mobile.hero.subtitle')}
				</p>
				<div class="hero__actions">
					<Button href="https://apps.apple.com" variant="primary" size="lg" class="btn-with-icon">
						<i class="fa-brands fa-apple"></i>
						<span>{$_('products.mobile.hero.iosButton')}</span>
					</Button>
					<Button href="https://play.google.com" variant="primary" size="lg" class="btn-with-icon">
						<i class="fa-brands fa-google-play"></i>
						<span>{$_('products.mobile.hero.androidButton')}</span>
					</Button>
				</div>
				<div class="hero__devices">
					<div class="device-wrapper device-wrapper--left">
						<img src="/images/mobile-1.png" alt="Heyweek mobile app time tracking" class="device" />
					</div>
					<div class="device-wrapper device-wrapper--right">
						<img src="/images/mobile-2.png" alt="Heyweek mobile app project view" class="device" />
					</div>
				</div>
			</div>
		</div>
	</section>

	<!-- Features Section -->
	<section class="features">
		<div class="container">
			<div class="features__header">
				<h2 class="features__title">{$_('products.mobile.features.title')}</h2>
				<p class="features__subtitle">{$_('products.mobile.features.subtitle')}</p>
			</div>
			
			<div class="features__grid">
				{#each features as feature}
					<Card class="feature-card">
						<div class="feature-card__icon">
							<Icon name={feature.icon} size={24} />
						</div>
						<h3 class="feature-card__title">
							{feature.title}
						</h3>
						<p class="feature-card__description">
							{feature.description}
						</p>
					</Card>
				{/each}
			</div>
		</div>
	</section>

	<!-- Platforms Section -->
	<section class="platforms">
		<div class="container">
			<div class="platforms__header">
				<h2 class="platforms__title">{$_('products.mobile.platforms.title')}</h2>
				<p class="platforms__subtitle">{$_('products.mobile.platforms.subtitle')}</p>
			</div>
			
			<div class="platforms__grid">
				<!-- iOS -->
				<div class="platform">
					<div class="platform__header">
						<i class="fa-brands fa-apple platform__icon"></i>
						<h3 class="platform__title">{$_('products.mobile.platforms.ios.name')}</h3>
					</div>
					<p class="platform__requirement">
						{$_('products.mobile.platforms.requirements')}: {$_('products.mobile.platforms.ios.requirements')}
					</p>
				</div>

				<!-- Android -->
				<div class="platform">
					<div class="platform__header">
						<i class="fa-brands fa-android platform__icon"></i>
						<h3 class="platform__title">{$_('products.mobile.platforms.android.name')}</h3>
					</div>
					<p class="platform__requirement">
						{$_('products.mobile.platforms.requirements')}: {$_('products.mobile.platforms.android.requirements')}
					</p>
				</div>
			</div>
		</div>
	</section>

	<!-- Sync Section -->
	<section class="sync">
		<div class="container">
			<div class="sync__content">
				<h2 class="sync__title">{$_('products.mobile.sync.title')}</h2>
				<p class="sync__description">{$_('products.mobile.sync.description')}</p>
				<div class="sync__features">
					<div class="sync__feature">
						<Icon name="refresh-cw" size={20} />
						<span>{$_('products.mobile.sync.feature1')}</span>
					</div>
					<div class="sync__feature">
						<Icon name="clock" size={20} />
						<span>{$_('products.mobile.sync.feature2')}</span>
					</div>
					<div class="sync__feature">
						<Icon name="users" size={20} />
						<span>{$_('products.mobile.sync.feature3')}</span>
					</div>
				</div>
			</div>
		</div>
	</section>

	<!-- CTA Section -->
	<section class="cta">
		<div class="container">
			<div class="cta__content">
				<h2 class="cta__title">{$_('products.mobile.cta.title')}</h2>
				<p class="cta__subtitle">{$_('products.mobile.cta.subtitle')}</p>
				<div class="cta__actions">
					<Button href="https://apps.apple.com" variant="secondary" size="lg" class="btn-with-icon">
						<i class="fa-brands fa-app-store-ios"></i>
						<span>{$_('products.mobile.cta.iosButton')}</span>
					</Button>
					<Button href="https://play.google.com" variant="secondary" size="lg" class="btn-with-icon">
						<i class="fa-brands fa-google-play"></i>
						<span>{$_('products.mobile.cta.androidButton')}</span>
					</Button>
				</div>
			</div>
		</div>
	</section>
</div>

<SeoContent slug="products-mobile" />

<Footer />

<style>
	.product-page {
		min-height: 100vh;
		padding-top: var(--space-8);
	}

	/* Override Tailwind's container with proper constraints */
	:global(.container) {
		max-width: 1200px;
		margin: 0 auto;
		padding: 0 1rem;
		width: 100%;
	}

	/* Hero Section */
	.hero {
		padding: var(--space-16) 0;
		background: linear-gradient(180deg, var(--bg-secondary) 0%, transparent 100%);
		text-align: center;
		overflow: hidden;
		position: relative;
	}

	.hero::before {
		content: '';
		position: absolute;
		top: 50%;
		left: 50%;
		transform: translate(-50%, -50%);
		width: 800px;
		height: 800px;
		background: radial-gradient(circle, 
			rgba(255, 94, 126, 0.03) 0%, 
			transparent 70%);
		pointer-events: none;
	}

	.hero__content {
		max-width: 800px;
		margin: 0 auto;
	}

	.hero__badge {
		display: inline-flex;
		align-items: center;
		gap: var(--space-2);
		padding: var(--space-1) var(--space-3);
		background: rgba(255, 94, 126, 0.1);
		color: rgb(255, 94, 126);
		border-radius: var(--radius-full);
		font-size: var(--font-size-sm);
		font-weight: var(--font-weight-medium);
		margin-bottom: var(--space-4);
	}

	:global(.dark) .hero__badge {
		background: rgba(255, 94, 126, 0.15);
		color: rgb(255, 140, 160);
	}

	.hero__title {
		font-size: var(--font-size-5xl);
		font-weight: var(--font-weight-bold);
		line-height: 1.1;
		color: var(--text-primary);
		margin-bottom: var(--space-6);
		letter-spacing: -0.02em;
	}

	.hero__subtitle {
		font-size: var(--font-size-lg);
		color: var(--text-secondary);
		line-height: 1.6;
		margin-bottom: var(--space-8);
		max-width: 600px;
		margin-left: auto;
		margin-right: auto;
	}

	.hero__actions {
		display: flex;
		gap: var(--space-4);
		justify-content: center;
		flex-wrap: wrap;
		margin-bottom: var(--space-12);
	}

	.hero__actions :global(.button) {
		display: inline-flex;
		align-items: center;
		gap: var(--space-2);
	}

	:global(.btn-with-icon i) {
		font-size: 1.25rem;
		display: inline-block;
	}

	.hero__devices {
		display: flex;
		justify-content: center;
		gap: var(--space-10);
		margin-top: var(--space-12);
		position: relative;
	}

	.device-wrapper {
		position: relative;
		padding: 12px;
		background: linear-gradient(135deg, 
			rgba(255, 94, 126, 0.05) 0%, 
			rgba(151, 71, 255, 0.05) 100%);
		border-radius: 24px;
		border: 1px solid rgba(255, 255, 255, 0.1);
	}

	:global(.dark) .device-wrapper {
		background: linear-gradient(135deg, 
			rgba(255, 94, 126, 0.08) 0%, 
			rgba(151, 71, 255, 0.08) 100%);
		border: 1px solid rgba(255, 255, 255, 0.05);
	}

	.device-wrapper--left {
		transform: rotate(-2deg) translateY(-8px);
	}

	.device-wrapper--right {
		transform: rotate(2deg) translateY(8px);
	}

	.device {
		width: 280px;
		height: auto;
		border-radius: 16px;
		box-shadow: 0 20px 40px rgba(0, 0, 0, 0.4);
		display: block;
	}

	/* Features Section */
	.features {
		padding: var(--space-20) 0;
	}

	.features__header {
		text-align: center;
		margin-bottom: var(--space-12);
	}

	.features__title {
		font-size: var(--font-size-3xl);
		font-weight: var(--font-weight-semibold);
		color: var(--text-primary);
		margin-bottom: var(--space-4);
	}

	.features__subtitle {
		font-size: var(--font-size-lg);
		color: var(--text-secondary);
		max-width: 600px;
		margin: 0 auto;
	}

	.features__grid {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
		gap: var(--space-6);
		max-width: 1200px;
		margin: 0 auto;
	}

	:global(.feature-card) {
		padding: var(--space-8);
		transition: transform 0.2s;
		text-align: center;
	}

	:global(.feature-card:hover) {
		transform: translateY(-4px);
	}

	.feature-card__icon {
		width: 48px;
		height: 48px;
		display: flex;
		align-items: center;
		justify-content: center;
		background: var(--accent-primary-muted);
		color: var(--accent-primary);
		border-radius: var(--radius-lg);
		margin: 0 auto var(--space-4);
	}

	.feature-card__title {
		font-size: var(--font-size-lg);
		font-weight: var(--font-weight-semibold);
		color: var(--text-primary);
		margin-bottom: var(--space-2);
	}

	.feature-card__description {
		font-size: var(--font-size-sm);
		color: var(--text-secondary);
		line-height: 1.6;
	}

	/* Platforms Section */
	.platforms {
		padding: var(--space-20) 0;
		background: var(--bg-secondary);
	}

	.platforms__header {
		text-align: center;
		margin-bottom: var(--space-12);
	}

	.platforms__title {
		font-size: var(--font-size-3xl);
		font-weight: var(--font-weight-semibold);
		color: var(--text-primary);
		margin-bottom: var(--space-4);
	}

	.platforms__subtitle {
		font-size: var(--font-size-lg);
		color: var(--text-secondary);
		max-width: 600px;
		margin: 0 auto;
	}

	.platforms__grid {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
		gap: var(--space-8);
		max-width: 900px;
		margin: 0 auto;
	}

	.platform {
		background: var(--bg-primary);
		border: 1px solid var(--border-light);
		border-radius: var(--radius-lg);
		padding: var(--space-8);
	}

	.platform__header {
		display: flex;
		align-items: center;
		gap: var(--space-4);
		margin-bottom: var(--space-4);
		color: var(--accent-primary);
	}

	.platform__icon {
		font-size: 2rem;
		color: var(--accent-primary);
	}

	.platform__title {
		font-size: var(--font-size-2xl);
		font-weight: var(--font-weight-bold);
		color: var(--text-primary);
	}

	.platform__requirement {
		font-size: var(--font-size-sm);
		color: var(--text-secondary);
	}

	/* Sync Section */
	.sync {
		padding: var(--space-20) 0;
		text-align: center;
	}

	.sync__content {
		max-width: 800px;
		margin: 0 auto;
	}

	.sync__title {
		font-size: var(--font-size-3xl);
		font-weight: var(--font-weight-semibold);
		color: var(--text-primary);
		margin-bottom: var(--space-4);
	}

	.sync__description {
		font-size: var(--font-size-lg);
		color: var(--text-secondary);
		line-height: 1.6;
		margin-bottom: var(--space-8);
	}

	.sync__features {
		display: flex;
		flex-direction: column;
		gap: var(--space-4);
		max-width: 500px;
		margin: 0 auto;
	}

	.sync__feature {
		display: flex;
		align-items: center;
		gap: var(--space-3);
		padding: var(--space-4);
		background: var(--bg-secondary);
		border-radius: var(--radius-md);
		color: var(--text-primary);
		font-size: var(--font-size-base);
	}

	.sync__feature :global(svg) {
		color: var(--accent-primary);
	}

	/* CTA Section */
	.cta {
		padding: var(--space-20) 0;
		background: linear-gradient(135deg, var(--accent-primary), var(--accent-secondary));
		text-align: center;
	}

	.cta__content {
		max-width: 700px;
		margin: 0 auto;
	}

	.cta__title {
		font-size: var(--font-size-3xl);
		font-weight: var(--font-weight-semibold);
		color: white;
		margin-bottom: var(--space-4);
	}

	.cta__subtitle {
		font-size: var(--font-size-lg);
		color: white;
		opacity: 0.9;
		margin-bottom: var(--space-8);
		line-height: 1.6;
	}

	.cta__actions {
		display: flex;
		gap: var(--space-4);
		justify-content: center;
		flex-wrap: wrap;
	}

	.cta__actions :global(.button) {
		display: inline-flex;
		align-items: center;
		gap: var(--space-2);
	}

	.cta__actions :global(.btn-with-icon i) {
		font-size: 1.25rem;
		display: inline-block;
	}

	/* Icons for platforms - need to add these to Icon component */
	:global(.icon-apple),
	:global(.icon-play),
	:global(.icon-smartphone) {
		flex-shrink: 0;
	}

	/* Responsive Design */
	@media (max-width: 768px) {
		.hero__title {
			font-size: 2rem;
		}

		.hero__devices {
			flex-direction: column;
			align-items: center;
			gap: var(--space-6);
		}

		.device-wrapper {
			padding: 8px;
		}

		.device-wrapper--left,
		.device-wrapper--right {
			transform: none;
		}

		.device {
			width: 100%;
			max-width: 280px;
		}

		.features__grid {
			grid-template-columns: 1fr;
		}

		.platforms__grid {
			grid-template-columns: 1fr;
		}

		.sync__features {
			text-align: left;
		}
	}
</style>